import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation  } from "react-router-dom";

import FormContentManager from "../../components/common/form/FormContentManager";
import TableCell from "../../components/common/ui/TableCell";

import WorkOrderSelect from "../../components/const/Order/WorkOrderSelect";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import ReferenceModal from "../../components/approval/ReferenceModal";
import DocSummaryCard from "../../components/common/form/DocSummary";
import PhotoBoardSelect from "../../components/const/Photo/PhotoBoardSelect";
import PhotoBoardReadonly from "../../components/const/Photo/PhotoBoardReadonly";
import { getCompletionReportDetail } from "../../api/const";
import PeopleCellLoader from "../../components/common/people/PeopleCellLoader";

export default function OrderCompleteNew() {
  const { id: projectId, dns } = useParams();
  const { state } = useLocation();
  const editorRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    workorder_dns: null,
    photoboard: null,
  });

  // 에러 메시지 상태
  const [errors, setErrors] = useState({
    title: "",
    approver: "",
    workorder: "",
  });

  // 작업지시서 선택 모달
  const [selectedOrder, setSelectedOrder] = useState(state?.order ?? null);
  const [openOrderModal, setOpenOrderModal] = useState(false);

  // 사진대지 선택 모달
  const [openPhotoModal, setOpenPhotoModal] = useState(false);

  // 승인자 선택
  const [reviewer, setReviewer] = useState([])

  useEffect(() => {
    if (!dns && state?.order) {
      setForm((prev) => ({
        ...prev,
        workorder_dns: state.order.dns,
      }));
      setSelectedOrder(state.order);
    }
  }, [dns, state]);

  // Edit 모드: 서버 데이터로 채우기
  useEffect(() => {
    if (!dns) return;

    const loadData = async () => {
      const res = await getCompletionReportDetail(dns);
      const data = res.data || res;

      setForm({
        title: data.title,
        workorder_dns: data.meta.workorder,
        photoboard: data.meta.photoboard,
      });

      setSelectedOrder({
        dns: data.meta.workorder,
        title: data.workorder_title,
      });
    };

    loadData();
  }, [dns]);

  /* ---------------------------------------
      Payload
  --------------------------------------- */
  const getBody = () => {
    const detailHtml = editorRef.current?.getContent() || "";

    return {
      values: {
        title: form.title,
        doc_type: "ordercomplete",
        dns: dns || null,

        confirmer: form.confirmer,
        confirmer_name: form.confirmer_name,

        workorder: form.workorder_dns,

        meta: {
          detail: detailHtml,
          workorder: form.workorder_dns,
          photoboard: form.photoboard,
        },
      }

    };
  };

  /* ---------------------------------------
      validate()
  --------------------------------------- */
  const validateForm = () => {
    const newErrors = {
      title: "",
      approver: "",
      workorder: "",
    };

    if (!form.title.trim()) newErrors.title = "제목을 입력하세요.";
    if (!reviewer?.id) newErrors.approver = "승인자를 선택하세요.";
    if (!form.workorder_dns) newErrors.workorder = "작업지시서를 선택하세요.";

    setErrors(newErrors);

    return Object.values(newErrors).every((e) => e === "");
  };

  const redirectTo = `/project/${projectId}/const/ordercomplete/`;

  return (
    <FormContentManager
      params={{
        project: projectId,
        module: "const",
        doc_type: "ordercomplete",
      }}
      getBody={getBody}
      redirectTo={redirectTo}
      validate={validateForm}
      docName="완료보고서"
      docDescribe="작업지시서의 완료보고서를 등록하세요."
      reviewer = {reviewer}
      summaryComponent={
        <DocSummaryCard
          title="작업지시서 요약"
          sections={[
            { label: "제목", value: form.title },
            { label: "승인자", value: reviewer?.name || "-"},
            { label: "연결된 작업지시서", value: selectedOrder?.title || "-" },
            { label: "사진대지", value: form.photoboard || "-" },
          ]}
        />
      }
    >
      <div>

        {/* ==========================
            입력 테이블
        ========================== */}
        <table className="w-full text-sm border border-black mb-6">
          <tbody className="divide-y">

            {/* 제목 & 승인자 */}
            <tr className="divide-x">
              <TableCell
                label="제목"
                required
                editing
                input={
                  <div>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                      }
                      className="w-full border rounded px-2 py-1"
                    />
                    {errors.title && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.title}
                      </p>
                    )}
                  </div>
                }
              />

              {/* 승인자 */}
              <TableCell
                colSpan={3}
                label="승인자"
                editing
                input={
                  <PeopleCellLoader
                    dns={dns}
                    value={reviewer}
                    single
                    onChange={(list) => setReviewer(list)}
                  />
                }
              />
            </tr>

            {/* 작업지시서 */}
            <tr className="divide-x">
              <TableCell
                label="작업지시서"
                required
                colSpan={3}
                editing                
                input={
                  <div>
                    <input
                      readOnly
                      value={
                        selectedOrder
                          ? selectedOrder.title
                          : "작업지시서를 선택하세요"
                      }
                      onClick={() => setOpenOrderModal(true)}
                      className="w-full border rounded px-2 py-1 cursor-pointer hover:bg-gray-50"
                    />
                    {errors.workorder && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.workorder}
                      </p>
                    )}
                  </div>
                }
              />
            </tr>
          </tbody>
        </table>

        {/* ==========================
            TinyMCE
        ========================== */}
        <section>
          <div className="mb-6 w-full border border-black text-sm">
            <div className="border-b border-black px-3 py-2 font-semibold bg-gray-100">
              상세 내용
            </div>

            <div className="px-3 py-3">
              <TinyMCEeditor height={500} editorRef={editorRef} />
            </div>
          </div>
        </section>

        {/* ==========================
            사진대지
        ========================== */}
        <div className="mb-6 border border-black">
          <div className="border-b border-black bg-gray-100 px-3 py-2 font-semibold">
            사진대지
          </div>

          <div className="px-3 py-4">
            {form.photoboard ? (
              <>
                <PhotoBoardReadonly photoboardId={form.photoboard} />

                <button
                  className="mt-3 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() => setOpenPhotoModal(true)}
                >
                  사진대지 변경
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-500 mb-2">
                  연결된 사진대지가 없습니다.
                </p>

                <button
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() => setOpenPhotoModal(true)}
                >
                  사진대지 선택
                </button>
              </>
            )}
          </div>
        </div>

      </div>
      {/* 작업지시서 모달 */}
      <WorkOrderSelect
        isOpen={openOrderModal}
        projectId={projectId}
        onClose={() => setOpenOrderModal(false)}
        onSelect={(row) => {
          setSelectedOrder(row);
          setForm({ ...form, workorder_dns: row.dns });
          setErrors({ ...errors, workorder: "" });
        }}
      />

      {/* 사진대지 선택 모달 */}
      <PhotoBoardSelect
        isOpen={openPhotoModal}
        onClose={() => setOpenPhotoModal(false)}
        onSelect={(board) => {
          setForm({ ...form, photoboard: board.id });
          setOpenPhotoModal(false);
        }}
      />
    </FormContentManager>
  );
}
